const contactFieldLabels = {
  "official.first_name": "Authorized Official First Name",
  "official.last_name": "Authorized Official Last Name",
  "official.title": "Authorized Official Job Title",
  "official.phone_number": "Authorized Official Phone Number",
  "official.email_address": "Authorized Official Email Address",
  "official.fax_number": "Authorized Official Fax Number",
  "primary.first_name": "Primary Contact First Name",
  "primary.last_name": "Primary Contact Last Name",
  "primary.title": "Primary Contact Job Title",
  "primary.phone_number": "Primary Contact Phone Number",
  "primary.email_address": "Primary Contact Email Address",
} as const;

export type ContactInformationField = keyof typeof contactFieldLabels;

const serverFieldMap: Record<string, ContactInformationField> = {
  authorized_official_first_name: "official.first_name",
  authorized_official_last_name: "official.last_name",
  authorized_official_title: "official.title",
  authorized_official_phone: "official.phone_number",
  authorized_official_email: "official.email_address",
  authorized_official_fax: "official.fax_number",
  primary_contact_first_name: "primary.first_name",
  primary_contact_last_name: "primary.last_name",
  primary_contact_title: "primary.title",
  primary_contact_phone: "primary.phone_number",
  primary_contact_email: "primary.email_address",
};

export const sameAsPrimaryContactMessage = "Cannot be the same as Primary Contact";
export const sameAsAuthorizedOfficialMessage = "Cannot be the same as Authorized Official";

const sameContactErrorMessages = new Set([
  sameAsPrimaryContactMessage,
  sameAsAuthorizedOfficialMessage,
]);

const sameContactNameMessage =
  "Authorized Official and Primary Contact names must be different.";
const sameContactPhoneMessage =
  "Authorized Official and Primary Contact phone numbers must be different.";

export const contactInformationErrorMessage =
  "Failed to save contact information. Please try again later.";

const sameContactMessages: Partial<Record<ContactInformationField, string>> = {
  "official.first_name": sameContactNameMessage,
  "official.last_name": sameContactNameMessage,
  "primary.first_name": sameContactNameMessage,
  "primary.last_name": sameContactNameMessage,
  "official.phone_number": sameContactPhoneMessage,
  "primary.phone_number": sameContactPhoneMessage,
};

const isContactInformationField = (
  path: string,
): path is ContactInformationField => {
  return path in contactFieldLabels;
};

const isNestedErrorRecord = (
  value: unknown,
): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

export const getServerFormField = (
  field: string,
): ContactInformationField | undefined => {
  return serverFieldMap[field];
};

export const getFieldBannerMessage = (path: string, message: string): string => {
  if (!isContactInformationField(path)) {
    return message;
  }

  const sameContactMessage = sameContactMessages[path];

  if (sameContactMessage && sameContactErrorMessages.has(message)) {
    return sameContactMessage;
  }

  return `${contactFieldLabels[path]}: ${message}`;
};

export const getBannerMessages = (
  errors: Record<string, unknown>,
  prefix = "",
): string[] => {
  const bannerMessages = new Set<string>();

  Object.entries(errors).forEach(([key, value]) => {
    if (!isNestedErrorRecord(value)) {
      return;
    }

    const nextPath = prefix ? `${prefix}.${key}` : key;
    const message = value.message;

    if (typeof message === "string") {
      bannerMessages.add(getFieldBannerMessage(nextPath, message));
      return;
    }

    getBannerMessages(value, nextPath).forEach((nestedMessage) => {
      bannerMessages.add(nestedMessage);
    });
  });

  return Array.from(bannerMessages);
};
